import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FiShield,
  FiCheckCircle,
  FiAlertCircle,
  FiLock,
  FiRefreshCw,
  FiCopy
} from 'react-icons/fi';
import { authService } from '../services/auth';
import {
  get2FAStatus,
  setup2FA,
  verify2FA,
  disable2FA,
  TwoFactorSetupResponse
} from '../services/api';
import './ProfileSettings.css';

type Message = { text: string; type: 'success' | 'error' } | null;

const ProfileSettings = () => {
  // --- Alteração de senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<Message>(null);
  const [loading, setLoading] = useState(false);

  // --- 2FA
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorStep, setTwoFactorStep] = useState<'idle' | 'enrolling'>('idle');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorMessage, setTwoFactorMessage] = useState<Message>(null);
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  // --- Desativação
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    const res = await get2FAStatus();
    if (res.success && res.status) {
      setTwoFactorEnabled(res.status.enabled);
    }
    setStatusLoading(false);
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const target = document.getElementById(id);
    if (target) {
      window.requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ text: 'A nova senha e a confirmação não conferem', type: 'error' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ text: 'A nova senha deve ter pelo menos 6 caracteres', type: 'error' });
      return;
    }

    setLoading(true);

    try {
      const success = await authService.changePassword(currentPassword, newPassword);

      if (success) {
        setMessage({ text: 'Senha alterada com sucesso', type: 'success' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ text: 'Erro ao alterar senha. Verifique a senha atual.', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Ocorreu um erro ao processar a solicitação', type: 'error' });
      console.error('Erro ao alterar senha:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEnrollment = async () => {
    setTwoFactorMessage(null);
    setTwoFactorLoading(true);
    const res = await setup2FA();
    setTwoFactorLoading(false);
    if (res.success && res.data) {
      setSetupData(res.data);
      setTwoFactorStep('enrolling');
      setTwoFactorCode('');
    } else {
      setTwoFactorMessage({
        text: res.error || 'Não foi possível iniciar a configuração do 2FA.',
        type: 'error'
      });
    }
  };

  const handleConfirm2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorMessage(null);

    if (twoFactorCode.length !== 6) {
      setTwoFactorMessage({ text: 'Informe os 6 dígitos do código gerado.', type: 'error' });
      return;
    }

    setTwoFactorLoading(true);
    const res = await verify2FA(twoFactorCode);
    setTwoFactorLoading(false);

    if (res.success) {
      setTwoFactorEnabled(true);
      setTwoFactorStep('idle');
      setTwoFactorCode('');
      setSetupData(null);
      setTwoFactorMessage({
        text: 'Verificação em duas etapas ativada com sucesso.',
        type: 'success'
      });
    } else {
      setTwoFactorMessage({
        text: res.error || 'Código inválido. Tente novamente.',
        type: 'error'
      });
    }
  };

  const handleDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setTwoFactorMessage(null);

    if (!disablePassword) {
      setTwoFactorMessage({ text: 'Informe sua senha para desativar o 2FA.', type: 'error' });
      return;
    }

    setTwoFactorLoading(true);
    const res = await disable2FA(disablePassword, disableCode || undefined);
    setTwoFactorLoading(false);

    if (res.success) {
      setTwoFactorEnabled(false);
      setShowDisableForm(false);
      setDisablePassword('');
      setDisableCode('');
      setTwoFactorMessage({
        text: 'Verificação em duas etapas desativada.',
        type: 'success'
      });
    } else {
      setTwoFactorMessage({
        text: res.error || 'Não foi possível desativar o 2FA.',
        type: 'error'
      });
    }
  };

  const handle2FACodeChange = (value: string) => {
    setTwoFactorCode(value.replace(/\D/g, '').slice(0, 6));
  };

  const handleDisableCodeChange = (value: string) => {
    setDisableCode(value.replace(/\D/g, '').slice(0, 6));
  };

  const copySecret = async () => {
    if (!setupData?.secret) return;
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setTwoFactorMessage({ text: 'Chave copiada para a área de transferência.', type: 'success' });
    } catch {
      setTwoFactorMessage({ text: 'Não foi possível copiar a chave.', type: 'error' });
    }
  };

  return (
    <div className="profile-settings">
      {/* ============== Alterar senha ============== */}
      <section className="settings-block">
        <header className="settings-block-header">
          <div className="settings-icon">
            <FiLock />
          </div>
          <div>
            <h2>Alterar Senha</h2>
            <p>Mantenha sua conta segura usando uma senha forte.</p>
          </div>
        </header>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="currentPassword">Senha atual</label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Nova senha</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar nova senha</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={loading}
            />
          </div>

          {message && (
            <div className={`message ${message.type}`}>
              {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
              <span>{message.text}</span>
            </div>
          )}

          <button type="submit" disabled={loading}>
            {loading ? 'Processando...' : 'Alterar senha'}
          </button>
        </form>
      </section>

      {/* ============== 2FA ============== */}
      <section className="settings-block" id="twofa">
        <header className="settings-block-header">
          <div className="settings-icon">
            <FiShield />
          </div>
          <div>
            <h2>Verificação em duas etapas (2FA)</h2>
            <p>
              Adicione uma camada extra de segurança ao seu login usando um app autenticador como
              Google Authenticator, Authy ou 1Password.
            </p>
          </div>
        </header>

        <div className="twofa-status">
          <span className={`twofa-pill ${twoFactorEnabled ? 'on' : 'off'}`}>
            {statusLoading ? 'Verificando...' : twoFactorEnabled ? 'Ativado' : 'Desativado'}
          </span>
          {!twoFactorEnabled && twoFactorStep === 'idle' && (
            <button
              type="button"
              className="twofa-action primary"
              onClick={handleStartEnrollment}
              disabled={twoFactorLoading || statusLoading}
            >
              {twoFactorLoading ? 'Gerando...' : 'Ativar 2FA'}
            </button>
          )}
          {twoFactorEnabled && !showDisableForm && (
            <button
              type="button"
              className="twofa-action danger"
              onClick={() => {
                setShowDisableForm(true);
                setTwoFactorMessage(null);
              }}
            >
              Desativar
            </button>
          )}
        </div>

        {twoFactorStep === 'enrolling' && setupData && (
          <div className="twofa-enroll">
            <ol className="twofa-steps">
              <li>Abra o app autenticador (Google Authenticator, Authy, 1Password, etc).</li>
              <li>Escaneie o QR Code abaixo ou insira a chave manualmente.</li>
              <li>Informe o código de 6 dígitos gerado pelo app para confirmar.</li>
            </ol>

            <div className="twofa-qr">
              <img
                src={setupData.qrCodeDataUrl}
                alt="QR Code para configuração do 2FA"
                className="twofa-qr-image"
              />
              <div className="twofa-secret">
                <span className="twofa-secret-label">Chave manual</span>
                <div className="twofa-secret-row">
                  <code>{setupData.secret}</code>
                  <button
                    type="button"
                    className="twofa-action ghost twofa-action-icon"
                    onClick={copySecret}
                    title="Copiar chave"
                    aria-label="Copiar chave"
                  >
                    <FiCopy />
                  </button>
                </div>
              </div>
            </div>

            <form onSubmit={handleConfirm2FA} className="twofa-confirm-form">
              <div className="form-group">
                <label htmlFor="twoFactorCode">Código de verificação</label>
                <input
                  type="text"
                  id="twoFactorCode"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={twoFactorCode}
                  onChange={(e) => handle2FACodeChange(e.target.value)}
                  autoComplete="one-time-code"
                  className="twofa-code-input"
                  disabled={twoFactorLoading}
                />
              </div>

              <div className="twofa-actions">
                <button
                  type="button"
                  className="twofa-action ghost"
                  onClick={() => {
                    setTwoFactorStep('idle');
                    setTwoFactorCode('');
                    setSetupData(null);
                  }}
                  disabled={twoFactorLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="twofa-action ghost"
                  onClick={handleStartEnrollment}
                  disabled={twoFactorLoading}
                  title="Gerar nova chave"
                >
                  <FiRefreshCw />
                </button>
                <button type="submit" className="twofa-action primary" disabled={twoFactorLoading}>
                  {twoFactorLoading ? 'Confirmando...' : 'Confirmar e ativar'}
                </button>
              </div>
            </form>
          </div>
        )}

        {twoFactorEnabled && showDisableForm && (
          <form onSubmit={handleDisable2FA} className="twofa-enroll">
            <p className="twofa-disable-hint">
              Para desativar a verificação em duas etapas, confirme com sua senha atual e o código
              do autenticador.
            </p>

            <div className="form-group">
              <label htmlFor="disablePassword">Senha atual</label>
              <input
                type="password"
                id="disablePassword"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={twoFactorLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="disableCode">Código atual do autenticador</label>
              <input
                type="text"
                id="disableCode"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={(e) => handleDisableCodeChange(e.target.value)}
                autoComplete="one-time-code"
                className="twofa-code-input"
                disabled={twoFactorLoading}
              />
            </div>

            <div className="twofa-actions">
              <button
                type="button"
                className="twofa-action ghost"
                onClick={() => {
                  setShowDisableForm(false);
                  setDisablePassword('');
                  setDisableCode('');
                }}
                disabled={twoFactorLoading}
              >
                Cancelar
              </button>
              <button type="submit" className="twofa-action danger" disabled={twoFactorLoading}>
                {twoFactorLoading ? 'Desativando...' : 'Confirmar desativação'}
              </button>
            </div>
          </form>
        )}

        {twoFactorMessage && (
          <div className={`message ${twoFactorMessage.type}`}>
            {twoFactorMessage.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
            <span>{twoFactorMessage.text}</span>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProfileSettings;
